import type { SizeStep } from '../../domain/annotation';
import { useT } from '../../i18n';
import { Icon, type IconName } from '../../ui/Icon';
import styles from './Toolbar.module.css';

/**
 * Die Werkzeugleiste der Markierebene.
 *
 * Reine Anzeige und Meldung — jeder Zustand liegt in der Grossansicht (`AnnotateSheet.tsx`)
 * und dahinter im Reducer. Die Leiste weiss von der Ebene selbst nichts.
 */

export type Werkzeug = 'arrow' | 'ellipse' | 'rect' | 'freehand' | 'text' | 'number' | 'cover';

/**
 * Sechs kontraststarke Farben. Weiss und Schwarz sind dabei, damit auf **jedem** Untergrund
 * etwas sichtbar bleibt: eine rote Marke auf einer blutigen Wunde sieht man nicht.
 */
export const FARBEN = ['#e0242b', '#1668dc', '#159c4a', '#f5a524', '#ffffff', '#111111'];

const STUFEN: SizeStep[] = ['klein', 'mittel', 'gross'];

export interface ToolbarProps {
  werkzeug: Werkzeug;
  farbe: string;
  stufe: SizeStep;
  kannUndo: boolean;
  kannRedo: boolean;
  hatAuswahl: boolean;
  /**
   * Ob die Grössenknöpfe überhaupt etwas bewirken. Falsch bei der Deckfläche: sie hat weder
   * Strich noch Schrift (s. `domain/annotation.ts::mitGroesse`). Tote Knöpfe anzubieten war
   * genau der Grund, warum der Nutzer sie für kaputt hielt.
   */
  groesseWirkt: boolean;
  /** Nur für „alles löschen" gebraucht — ohne `onAllesLoeschen` bedeutungslos. */
  hatMarken?: boolean;
  gesperrt: boolean;
  onWerkzeug: (w: Werkzeug) => void;
  onFarbe: (f: string) => void;
  onStufe: (s: SizeStep) => void;
  onUndo: () => void;
  onRedo: () => void;
  onMarkeLoeschen: () => void;
  /**
   * „Alles löschen".
   *
   * ⚠️ **Optional, und das Fehlen ist die Aussage.** Im Video-Annotationseditor gibt es den
   * Weg nicht: dort räumte derselbe Papierkorb **sämtliche Bänder** weg — ein Fehlgriff, der
   * sich kaum heilen lässt, und daneben liegt ohnehin ein Knopf, der genau ein Band löscht
   * (Entscheidung des Nutzers, 2026-09-20). Ein Schalter `zeigeAllesLoeschen` wäre dasselbe
   * in umständlich: wer die Aktion nicht reicht, hat sie nicht.
   */
  onAllesLoeschen?: () => void;
}

export function Toolbar(p: ToolbarProps) {
  const t = useT();

  /**
   * ⚠️ **Rechteck und Deckfläche stehen nebeneinander** (Befund des Nutzers, 2026-09-13). Es
   * ist dieselbe Form, einmal als Rahmen und einmal gefüllt; die Deckfläche stand vorher am
   * Ende, weil sie als einzige etwas *unkenntlich macht* statt hervorzuheben — eine
   * Gruppierung, die man der trennerlosen Leiste nicht ansieht.
   *
   * ⚠️ **Vier Zeichen waren irreführend** und sind am 2026-09-13 ersetzt: ein *Lesezeichen*
   * für den Pfeil (jetzt `arrow-up-right5`), ein *Auswahlknopf* für den Kreis (jetzt
   * `circle`), *Textzeilen* für die Beschriftung (jetzt `text-color`, ein „A") und eine
   * *nummerierte Liste* für die Nummer (jetzt `circle-1`, ein gefüllter Kreis mit Ziffer).
   *
   * `rect` (`checkbox-unchecked`) und `cover` (`stop2`) bleiben, wie sie waren: die
   * Quellnamen führen in die Irre, gezeichnet sind es ein leeres und ein gefülltes Quadrat —
   * genau das Paar, das hier gebraucht wird.
   */
  const werkzeuge: { wert: Werkzeug; icon: IconName; text: string }[] = [
    { wert: 'arrow', icon: 'markArrow', text: t.annotate.toolArrow },
    { wert: 'ellipse', icon: 'markCircle', text: t.annotate.toolEllipse },
    { wert: 'rect', icon: 'rect', text: t.annotate.toolRect },
    { wert: 'cover', icon: 'cover', text: t.annotate.toolCover },
    { wert: 'freehand', icon: 'scribble', text: t.annotate.toolFreehand },
    { wert: 'text', icon: 'markText', text: t.annotate.toolText },
    { wert: 'number', icon: 'markNumber', text: t.annotate.toolNumber },
  ];

  const stufenText: Record<SizeStep, string> = {
    klein: t.annotate.sizeSmall,
    mittel: t.annotate.sizeMedium,
    gross: t.annotate.sizeLarge,
  };

  return (
    <div className={styles.leiste}>
      <div className={styles.gruppe} role="group" aria-label={t.annotate.title}>
        {werkzeuge.map((w) => (
          <button
            key={w.wert}
            type="button"
            className={`tap ${styles.werkzeug} ${p.werkzeug === w.wert ? styles.aktiv : ''}`}
            onClick={() => p.onWerkzeug(w.wert)}
            disabled={p.gesperrt}
            aria-pressed={p.werkzeug === w.wert}
            aria-label={w.text}
            title={w.text}
          >
            <Icon name={w.icon} size={19} />
          </button>
        ))}
      </div>

      <div className={styles.gruppe} role="group" aria-label={t.annotate.color}>
        {FARBEN.map((f) => (
          <button
            key={f}
            type="button"
            className={`tap ${styles.farbe} ${p.farbe === f ? styles.farbeAktiv : ''}`}
            style={{ background: f }}
            onClick={() => p.onFarbe(f)}
            disabled={p.gesperrt}
            aria-pressed={p.farbe === f}
            aria-label={f}
          />
        ))}
      </div>

      <div className={styles.gruppe} role="group" aria-label={t.annotate.size}>
        {STUFEN.map((s) => (
          <button
            key={s}
            type="button"
            className={`tap ${styles.stufe} ${p.stufe === s ? styles.aktiv : ''}`}
            onClick={() => p.onStufe(s)}
            disabled={p.gesperrt || !p.groesseWirkt}
            aria-pressed={p.stufe === s}
          >
            {stufenText[s]}
          </button>
        ))}
      </div>

      <div className={styles.luecke} />

      <div className={styles.gruppe}>
        <button
          type="button"
          className={`tap ${styles.werkzeug}`}
          onClick={p.onUndo}
          disabled={p.gesperrt || !p.kannUndo}
          aria-label={t.annotate.undo}
          title={t.annotate.undo}
        >
          <Icon name="undo" size={19} />
        </button>
        <button
          type="button"
          className={`tap ${styles.werkzeug}`}
          onClick={p.onRedo}
          disabled={p.gesperrt || !p.kannRedo}
          aria-label={t.annotate.redo}
          title={t.annotate.redo}
        >
          <Icon name="redo" size={19} />
        </button>
        {/* ⚠️ **Zwei Knöpfe, nicht einer mit wechselnder Bedeutung.** Bis zum 2026-09-13 löschte
            derselbe Papierkorb mal die ausgewählte Marke und mal alle — man sah ihm nicht an,
            welches gerade galt. Das Kreuz nimmt die Auswahl weg, der Papierkorb das ganze
            Bild; er fragt weiterhin nach. Den Papierkorb gibt es nur, wo er gereicht wird. */}
        <button
          type="button"
          className={`tap ${styles.werkzeug} ${styles.gefahr}`}
          onClick={p.onMarkeLoeschen}
          disabled={p.gesperrt || !p.hatAuswahl}
          aria-label={t.annotate.deleteMarker}
          title={t.annotate.deleteMarker}
        >
          <Icon name="close" size={18} />
        </button>
        {p.onAllesLoeschen && (
          <button
            type="button"
            className={`tap ${styles.werkzeug} ${styles.gefahr}`}
            onClick={p.onAllesLoeschen}
            disabled={p.gesperrt || !p.hatMarken}
            aria-label={t.annotate.clearAll}
            title={t.annotate.clearAll}
          >
            <Icon name="trash" size={18} />
          </button>
        )}
      </div>
    </div>
  );
}
